import { Button, List, ListBody, ListCell, ListHead, ListHeadCell, ListRow, Loader } from '@plaidev/sour-react';
import type { ReactElement } from 'react';
import { DomainV2 } from 'troppus-common/domain';

import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;
import ImportTableDefinitionRevision = DomainV2.Import.ImportTableDefinitionRevision;
import ImportTask = DomainV2.Import.ImportTask;

export type RevisionHistorySectionProps = {
  revisions: ImportTableDefinitionRevision[];
  isPending: boolean;
  activeRevisionId: ImportTableDefinitionRevision.Id | undefined;
  lastRevisionId: ImportTableDefinitionRevision.Id;
  memberNameById?: Map<string, string>;
  creationTaskByRevisionId: Map<ImportTableDefinitionRevision.Id, ImportTask>;
  onNavigateToTaskList: (revisionId: ImportTableDefinitionRevision.Id) => void;
};

const compatibilityLabel = (t: ImportTableDefinition.LoadMode | string): string => {
  if (t === 'initial') return '初期';
  if (t === 'compatible') return '互換';
  if (t === 'possiblyBreaking') return '互換性なし';
  return t;
};

const compatibilityBadgeClass = (t: string): string => {
  if (t === 'initial') return 'bg-subtle text-muted-light';
  if (t === 'compatible') return 'bg-primary-light text-primary';
  if (t === 'possiblyBreaking') return 'bg-negative-light text-negative';
  return 'bg-subtle text-muted-light';
};

export function RevisionHistorySection({
  revisions,
  isPending,
  activeRevisionId,
  lastRevisionId,
  memberNameById,
  creationTaskByRevisionId,
  onNavigateToTaskList,
}: RevisionHistorySectionProps): ReactElement {
  const sorted = [...revisions].sort((a, b) => b.revisionNumber - a.revisionNumber);
  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-default">バージョン履歴</h2>
      <div className="rounded-lg border border-default p-4">
        {isPending ? (
          <div className="flex justify-center py-6">
            <Loader />
          </div>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-muted-light">バージョン履歴がありません。</p>
        ) : (
          <List>
            <ListHead>
              <ListRow>
                <ListHeadCell width={72}>#</ListHeadCell>
                <ListHeadCell width={140}>状態</ListHeadCell>
                <ListHeadCell width={120}>互換性</ListHeadCell>
                <ListHeadCell width={160}>作成日時</ListHeadCell>
                <ListHeadCell>作成者</ListHeadCell>
                <ListHeadCell width={132}>作成時タスク</ListHeadCell>
              </ListRow>
            </ListHead>
            <ListBody>
              {sorted.map((rev) => {
                const isActive = rev.id === activeRevisionId;
                const isLast = rev.id === lastRevisionId;
                const creationTask = creationTaskByRevisionId.get(rev.id);
                return (
                  <ListRow key={rev.id}>
                    <ListCell width={72}>
                      <span className="text-sm font-medium text-default">v{rev.revisionNumber}</span>
                    </ListCell>
                    <ListCell width={140}>
                      <span className="flex flex-wrap gap-1">
                        {isActive ? (
                          <span className="inline-flex items-center rounded-full bg-primary-light px-2 py-0.5 text-xs text-primary">
                            有効
                          </span>
                        ) : null}
                        {isLast ? (
                          <span className="inline-flex items-center rounded-full bg-notice-light px-2 py-0.5 text-xs text-notice">
                            最新
                          </span>
                        ) : null}
                        {!isActive && !isLast ? <span className="text-xs text-muted-light">—</span> : null}
                      </span>
                    </ListCell>
                    <ListCell width={120}>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${compatibilityBadgeClass(rev.後方互換性Type)}`}
                      >
                        {compatibilityLabel(rev.後方互換性Type)}
                      </span>
                    </ListCell>
                    <ListCell width={160}>
                      <span className="text-sm text-muted-light">{rev.createdAt.format('YYYY-MM-DD HH:mm')}</span>
                    </ListCell>
                    <ListCell>
                      <span className="block max-w-0 min-w-full truncate text-sm text-muted-light">
                        {memberNameById?.get(rev.createdBy) ?? rev.createdBy}
                      </span>
                    </ListCell>
                    <ListCell width={132}>
                      {creationTask ? (
                        <Button type="button" variant="quiet" size="sm" onClick={() => onNavigateToTaskList(rev.id)}>
                          タスク一覧へ
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-light">—</span>
                      )}
                    </ListCell>
                  </ListRow>
                );
              })}
            </ListBody>
          </List>
        )}
      </div>
    </section>
  );
}
